import { Component, OnInit } from '@angular/core';
import { AiTestingSettingsService } from './proxy/ai-testing-settings.service';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  AiTestingSettingsDto,
  AiTestingSettingsUpdateDto,
} from './proxy/ai-testing-settings.model';
import { forkJoin } from 'rxjs';
import { IntegrationService } from 'src/app/integrations/proxy/integration.service';
import {
  LlmConfigurationProviderModelsDto,
  LlmConfigurationSummaryDto,
} from 'src/app/integrations/proxy/integration.model';
import {
  AI_AGENT_FIELD_CONSTS,
  AI_AGENT_FIELD_NAMES,
  CUSTOM_MODEL,
} from 'src/app/ai-agents/proxy/ai-agents.consts';

@Component({
  selector: 'cai-ai-testing-settings',
  templateUrl: './ai-testing-settings.component.html',
  styleUrls: ['./ai-testing-settings.component.scss'],
  standalone: false,
})
export class AiTestingSettingsComponent implements OnInit {
  llmConfigurationList: LlmConfigurationSummaryDto[] = [];
  suggestedModels: LlmConfigurationProviderModelsDto[] = [];

  aiTestingSettingsForm: UntypedFormGroup;
  aiTestingSettings: AiTestingSettingsDto;
  isLoading = false;

  constructor(
    private aiTestingSettingsService: AiTestingSettingsService,
    private integrationService: IntegrationService,
    private fb: FormBuilder,
    private toaster: ToasterService,
  ) {}

  ngOnInit(): void {
    this.loadLlmConfigurations();
    this.buildForm();
  }

  buildForm() {
    this.aiTestingSettingsService.get().subscribe({
      next: (response) => {
        this.aiTestingSettings = response;

        this.aiTestingSettingsForm = this.fb.group({
          [AI_AGENT_FIELD_NAMES.llmConfiguration]: [
            this.aiTestingSettings.llmConfigurationId ?? null,
            [Validators.required],
          ],
          [AI_AGENT_FIELD_NAMES.llmModel]: [
            this.aiTestingSettings.llmModel ?? null,
            [Validators.required],
          ],
          [AI_AGENT_FIELD_NAMES.llmCustomModel]: [
            this.aiTestingSettings.isCustomModelSelected ? this.aiTestingSettings.llmModel : null,
            [Validators.maxLength(AI_AGENT_FIELD_CONSTS.llmModelMaxLength)],
          ],
          [AI_AGENT_FIELD_NAMES.reasoningEffort]: [this.aiTestingSettings.reasoningEffort],
        });
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  submitForm() {
    if (!this.aiTestingSettingsForm.valid) {
      return;
    }

    this.isLoading = true;

    const llmModel = this.aiTestingSettingsForm.get(AI_AGENT_FIELD_NAMES.llmModel).value;

    const aiTestingSettings: AiTestingSettingsUpdateDto = {
      llmConfigurationId: this.aiTestingSettingsForm.get(AI_AGENT_FIELD_NAMES.llmConfiguration)
        .value,
      llmModel: llmModel,
      isCustomModelSelected: llmModel === CUSTOM_MODEL ? true : false,
      reasoningEffort: this.aiTestingSettingsForm.get(AI_AGENT_FIELD_NAMES.reasoningEffort).value,
    };

    this.aiTestingSettingsService.createOrUpdate(aiTestingSettings).subscribe(
      () => {
        this.toaster.success('::CustomSettings:Save:Success');
        this.isLoading = false;
      },
      (error) => {
        this.toaster.error('::CustomSettings:Save:Error');
        console.log(error);
        this.isLoading = false;
      },
    );
  }

  isSaveDisabled() {
    return this.aiTestingSettingsForm.invalid;
  }

  private loadLlmConfigurations() {
    forkJoin({
      llmConfigurations: this.integrationService.getLlmConfigurationList({ maxResultCount: 1000 }),
      suggestedModels: this.integrationService.getSuggestedModels(),
    }).subscribe({
      next: ({ llmConfigurations, suggestedModels }) => {
        this.llmConfigurationList = llmConfigurations.items;
        this.suggestedModels = suggestedModels;
      },
      error: (error) => {
        console.error('Failed to load LLM configurations:', error);
      },
    });
  }
}
